import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { createApiClient } from '@/providers/ApiContextProvider/createApiClient';
import { assertIsDefined } from '@/helpers/assertions';
import { useNewAuth } from '@/providers/AuthProvider/NewAuthProvider.hooks';
import { usePermissionDenied } from '@/providers/PermissionDeniedProvider/PermissionDeniedContext.hooks';
import { IApiContext } from '@/providers/ApiContextProvider/ApiContext.types';

export const ApiContext = createContext<Partial<IApiContext>>({});

export const ApiProvider = ({ children }: PropsWithChildren) => {
  const { token, logOut } = useNewAuth();
  const { markPermissionDenied } = usePermissionDenied();

  const API = useMemo(() => {
    return createApiClient(token || '', logOut, markPermissionDenied);
  }, [token, logOut, markPermissionDenied]);

  return <ApiContext.Provider value={API}>{children}</ApiContext.Provider>;
};

export const useApi = () => {
  const {
    alertingApi,
    analystNotesApi,
    userApi,
    monitoringApi,
    entityIdsApi,
    calculatorsApi,
    risksApi,
    ratingsApi,
    docsApi,
    documentationApi,
    marketPricesApi,
    stablecoinApi,
  } = useContext(ApiContext);

  assertIsDefined(alertingApi, '"IApiContext.alertingApi has to be defined!"');
  assertIsDefined(analystNotesApi, '"IApiContext.analystNotesApi has to be defined!"');
  assertIsDefined(userApi, '"IApiContext.userApi has to be defined!"');
  assertIsDefined(monitoringApi, '"IApiContext.monitoringApi has to be defined!"');
  assertIsDefined(entityIdsApi, '"IApiContext.entityIdsApi has to be defined!"');
  assertIsDefined(calculatorsApi, '"IApiContext.calculatorsApi has to be defined!"');
  assertIsDefined(risksApi, '"IApiContext.risksApi has to be defined!"');
  assertIsDefined(ratingsApi, '"IApiContext.ratingsApi has to be defined!"');
  assertIsDefined(docsApi, '"IApiContext.docsApi has to be defined!"');
  assertIsDefined(documentationApi, '"IApiContext.documentationApi has to be defined!"');
  assertIsDefined(marketPricesApi, '"IApiContext.marketPricesApi has to be defined!"');
  assertIsDefined(stablecoinApi, '"IApiContext.stablecoinApi has to be defined!"');

  return {
    alertingApi,
    analystNotesApi,
    userApi,
    monitoringApi,
    entityIdsApi,
    calculatorsApi,
    risksApi,
    ratingsApi,
    docsApi,
    documentationApi,
    marketPricesApi,
    stablecoinApi,
  };
};
